/**
 * LightningTabs, Salesforce Lightning style tab strip + panel.
 *
 * Used on record pages (admin + user connector detail, future connector-set
 * detail) to separate Overview / Tools / Related / Advanced into clear
 * focused panels.
 *
 * Pattern follows SF Lightning's tab pill anchored to the top of the page
 * body, panels swap inline without re-routing. The `value`/`onChange` API
 * lets the parent persist the active tab in URL hash or component state.
 *
 * Tab visibility supports `hidden` flag so we can role-gate the Advanced
 * tab (e.g. only admins/owners see it) per Ean's 2026-06-03 #7.
 */

import { type ReactNode } from 'react';

import { MoreActionsMenu, type MoreMenuItem } from './MoreActionsMenu.tsx';

export interface LightningTab {
  /** Stable key. Used as the tab id and the data-tab marker. */
  id: string;
  /** Visible label. */
  label: string;
  /** Optional inline count badge (e.g. "Tools (7)"). */
  count?: number;
  /** Render content for this tab. */
  render: () => ReactNode;
  /** When true, the tab is omitted entirely. */
  hidden?: boolean;
  /** Push #28 — optional leading icon (e.g. ObjectIcon for pillar tabs in
   *  Catalog). Renders flush-left of the label. Tabs that don't pass this
   *  fall back to label-only chrome unchanged. */
  icon?: ReactNode;
}

interface LightningTabsProps {
  tabs: LightningTab[];
  /** Currently active tab id. */
  value: string;
  /** Notify parent when user clicks a different tab. */
  onChange: (id: string) => void;
  /**
   * Push 4-1o (2026-07-09) — optional tab-overflow cap. When set to N,
   * the first N visible tabs render as tab buttons and any additional
   * tabs collapse into a "More ▾" dropdown at the end of the strip.
   *
   * The active tab is ALWAYS kept in the visible strip (it swaps with
   * the last visible slot if the active tab would otherwise overflow),
   * so the user never sees "the tab I picked disappeared into a menu."
   *
   * When unset, all tabs render side-by-side (previous behavior).
   */
  maxVisible?: number;
  /**
   * Push 4-1o (2026-07-09) — trigger label for the overflow dropdown.
   * Defaults to "More" — override to "More tabs" or context-specific
   * framing.
   */
  moreLabel?: string;
}

export function LightningTabs({
  tabs,
  value,
  onChange,
  maxVisible,
  moreLabel = 'More',
}: LightningTabsProps) {
  const visible = tabs.filter((t) => t.hidden !== true);
  const active = visible.find((t) => t.id === value) ?? visible[0];

  // Push 4-1o overflow logic. When maxVisible is set and there are more
  // visible tabs than the cap, split into strip + overflow. The active
  // tab always lives in the strip so the user's current selection is
  // never buried under the More dropdown.
  const useOverflow =
    maxVisible !== undefined && visible.length > maxVisible;

  let stripTabs: LightningTab[] = visible;
  let overflowTabs: LightningTab[] = [];

  if (useOverflow && maxVisible !== undefined) {
    // First N tabs are the strip; the rest overflow.
    stripTabs = visible.slice(0, maxVisible);
    overflowTabs = visible.slice(maxVisible);

    // If the active tab is in overflow, swap it into the last strip slot
    // so the user's current position is always visible.
    const activeInOverflow = overflowTabs.some((t) => t.id === value);
    if (activeInOverflow) {
      const activeTab = overflowTabs.find((t) => t.id === value)!;
      const displaced = stripTabs[stripTabs.length - 1];
      if (displaced !== undefined) {
        stripTabs = [...stripTabs.slice(0, -1), activeTab];
        overflowTabs = overflowTabs
          .filter((t) => t.id !== value)
          .concat(displaced);
      }
    }
  }

  const overflowItems: MoreMenuItem[] = overflowTabs.map((t) => ({
    key: t.id,
    label: t.label + (t.count !== undefined ? ` (${t.count})` : ''),
    onClick: () => onChange(t.id),
  }));

  return (
    <div className="lightning-tabs">
      <div className="lightning-tabs-bar" role="tablist">
        {stripTabs.map((t) => {
          const isActive = active?.id === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${t.id}`}
              id={`tab-${t.id}`}
              className={`lightning-tab${isActive ? ' active' : ''}`}
              onClick={() => onChange(t.id)}
            >
              {t.icon !== undefined && <span aria-hidden="true">{t.icon}</span>}
              <span>{t.label}</span>
              {t.count !== undefined && (
                <span className="lightning-tab-count">{t.count}</span>
              )}
            </button>
          );
        })}
        {overflowItems.length > 0 && (
          <div className="lightning-tab-more" style={{ marginLeft: 4 }}>
            <MoreActionsMenu
              items={overflowItems}
              triggerLabel={moreLabel}
              variant="ghost"
              minWidth={180}
            />
          </div>
        )}
      </div>
      {active !== undefined && (
        <div
          role="tabpanel"
          id={`tabpanel-${active.id}`}
          aria-labelledby={`tab-${active.id}`}
          className="lightning-tab-panel"
        >
          {active.render()}
        </div>
      )}
    </div>
  );
}
